import { useQueryClient } from "@tanstack/react-query";
import { Button, Popconfirm, Table } from "antd";
import { Calendar, Edit, Eye, MapPin, Trash2 } from "lucide-react";
import { FC, useState } from "react";
import { useNotification } from "~/hooks";
import {
  useDeleteShuttle,
  useGetShuttles,
  VESSEL_KEYS,
} from "~/services/vessel";
import { TShuttle } from "~/types";
import { formatDate } from "~/utils";
import { StatusBadge } from "../status-badge";
import { useShuttleStore } from "../store/shuttle.store";
import { FilterShuttles } from "./filter-shuttles";

type Props = {};

export const TableShuttles: FC<Props> = () => {
  const { filters } = useShuttleStore();
  const { data: shuttles, isLoading } = useGetShuttles(filters);
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="font-medium text-base">List Shuttles</h3>
        <FilterShuttles />
      </div>

      <Table
        // size="small"
        columns={[
          { title: "ID", dataIndex: "shuttleRef", key: "shuttleRef" },
          { title: "Name", dataIndex: "project.name", key: "project.name" },
          {
            title: "Vessels",
            dataIndex: "vessels",
            key: "vessels",
            render: (_, record) =>
              record.vessels.map((vessel) => vessel.vesselRef).join(", "),
          },
          {
            title: "Dates",
            dataIndex: ["loadDate", "returnDate"],
            key: "dates",
            render: (_, record) => (
              <div className="flex items-center">
                <Calendar size={16} className="mr-2 text-gray-400" />
                {formatDate(record.loadDate)} to {formatDate(record.returnDate)}
              </div>
            ),
          },
          {
            title: "Destination",
            dataIndex: ["destination"],
            key: "destination",
            render: (destination) => (
              <div className="flex items-center">
                <MapPin size={16} className="mr-2 text-gray-400" />
                {destination}
              </div>
            ),
          },
          {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => <StatusBadge status={text} />,
          },
          {
            title: "Actions",
            key: "actions",
            render: (_, record) => <RowActions record={record} />,
          },
        ]}
        dataSource={shuttles?.items}
        loading={isLoading}
        rowKey="id"
        pagination={{
          pageSize: 10,
          total: shuttles?.meta.totalItems,
        }}
      />
    </div>
  );
};

const RowActions: FC<{ record: TShuttle }> = ({ record }) => {
  const qc = useQueryClient();

  const { success, error } = useNotification();

  const mutation = useDeleteShuttle();

  const [showConfirm, setShowConfirm] = useState(false);

  const { filters, openShuttleModal, openShuttleDetail } = useShuttleStore();

  return (
    <div className="flex space-x-2">
      <button
        onClick={() => openShuttleDetail(record.id)}
        className="text-gray-600 hover:text-gray-800"
        title="View"
      >
        <Eye size={16} />
      </button>
      <button
        onClick={() => openShuttleModal(record.id)}
        className="text-blue-600 hover:text-blue-800"
        title="Edit"
      >
        <Edit size={16} />
      </button>
      <Popconfirm
        open={showConfirm}
        title="Are you sure you want to delete this shuttle?"
        onConfirm={() => {
          mutation.mutate(record.id, {
            onSuccess: () => {
              setShowConfirm(false);
              success({
                message: "Shuttle deleted successfully",
              });
              qc.invalidateQueries({
                queryKey: VESSEL_KEYS.GET_SHUTTLES(filters),
              });
            },
            onError: (err) => {
              console.log("error", err);
              setShowConfirm(false);
              error({
                message: "Error deleting shuttle",
                description: err.message,
              });
            },
          });
        }}
        okButtonProps={{ loading: mutation.isPending }}
        okText="Yes"
        cancelText="No"
      >
        <Button type="link" danger onClick={() => setShowConfirm(true)}>
          <Trash2 size={16} />
        </Button>
      </Popconfirm>
    </div>
  );
};
